import type {
  SituationCategory,
  AudienceBackground,
  AimOfAnalysis,
  DegreeOfImpliciteness,
  DegreeOfAbstraction,
  DegreeOfIntention,
  SentenceStructure,
} from "@/generated/fastapi";

export const SITUATION_CATEGORY_OCCUPATION = "occupation" as const;
export const SITUATION_CATEGORY_EDUCATION = "education" as const;
export const SITUATION_CATEGORY_DEVELOPMENT = "development" as const;
export const SITUATION_CATEGORY_PREFERENCE = "preference" as const;

export const situationCategories: Record<SituationCategory, string> =
  Object.freeze({
    [SITUATION_CATEGORY_OCCUPATION]: "Occupation",
    [SITUATION_CATEGORY_EDUCATION]: "Education",
    [SITUATION_CATEGORY_DEVELOPMENT]: "Development",
    [SITUATION_CATEGORY_PREFERENCE]: "Preference",
  });

export const AIM_PRODUCT_PROMOTION = "product_promotion" as const;
export const AIM_USER_PROBLEM = "user_problem" as const;
export const AIM_TUTORIAL_GUIDE = "tutorial_guide" as const;
export const AIM_INDUSTRY_INSIGHT = "industry_insight" as const;
export const AIM_PROFESSIONAL_CRITIQUE = "professional_critique" as const;
export const AIM_TECHNICAL_EXPLANATION = "technical_explanation" as const;
export const AIM_PERSONAL_NARRATIVE = "personal_narrative" as const;
export const AIM_FUNNY_STORY = "funny_story" as const;
export const AIM_METHODICAL_APPROACH = "methodological_approach" as const;
export const AIM_PROVOCATIVE_OPINION = "provocative_opinion" as const;

export const aimOfAnalysisOptions: Record<AimOfAnalysis, string> =
  Object.freeze({
    [AIM_PRODUCT_PROMOTION]: "Product Promotion",
    [AIM_USER_PROBLEM]: "User Problem",
    [AIM_TUTORIAL_GUIDE]: "Tutorial Guide",
    [AIM_INDUSTRY_INSIGHT]: "Industry Insight",
    [AIM_PROFESSIONAL_CRITIQUE]: "Professional Critique",
    [AIM_TECHNICAL_EXPLANATION]: "Technical Explanation",
    [AIM_PERSONAL_NARRATIVE]: "Personal Narrative",
    [AIM_FUNNY_STORY]: "Funny Story",
    [AIM_METHODICAL_APPROACH]: "Methodological Approach",
    [AIM_PROVOCATIVE_OPINION]: "Provocative Opinion",
  });

export const AUDIENCE_GENERAL_PUBLIC = "general_public" as const;
export const AUDIENCE_INDUSTRY_PROFESSIONALS = "industry_professionals" as const;
export const AUDIENCE_TECHNICAL_EXPERTS = "technical_experts" as const;
export const AUDIENCE_ACADEMICS = "academics" as const;

export const audienceBackgroundOptions: Record<AudienceBackground, string> =
  Object.freeze({
    [AUDIENCE_GENERAL_PUBLIC]: "General Public",
    [AUDIENCE_INDUSTRY_PROFESSIONALS]: "Industry Professionals",
    [AUDIENCE_TECHNICAL_EXPERTS]: "Technical Experts",
    [AUDIENCE_ACADEMICS]: "Academics",
  });

export const IMPLICITNESS_EXPLICIT_ONLY = "explicit_only" as const;
export const IMPLICITNESS_MOSTLY_EXPLICIT = "mostly_explicit" as const;
export const IMPLICITNESS_PREDOMINANTLY_EXPLICIT =
  "predominantly_explicit" as const;
export const IMPLICITNESS_EXPLICIT_HEAVY = "explicit_heavy" as const;
export const IMPLICITNESS_SLIGHTLY_EXPLICIT = "slightly_explicit" as const;
export const IMPLICITNESS_BALANCED = "balanced" as const;
export const IMPLICITNESS_SLIGHTLY_IMPLICIT = "slightly_implicit" as const;
export const IMPLICITNESS_IMPLICIT_HEAVY = "implicit_heavy" as const;
export const IMPLICITNESS_PREDOMINANTLY_IMPLICIT =
  "predominantly_implicit" as const;
export const IMPLICITNESS_MOSTLY_IMPLICIT = "mostly_implicit" as const;
export const IMPLICITNESS_IMPLICIT_ONLY = "implicit_only" as const;

export const degreeOfImplicitenessOptions: Record<
  DegreeOfImpliciteness,
  string
> = Object.freeze({
  [IMPLICITNESS_EXPLICIT_ONLY]: "Explicit Only",
  [IMPLICITNESS_MOSTLY_EXPLICIT]: "Mostly Explicit",
  [IMPLICITNESS_PREDOMINANTLY_EXPLICIT]: "Predominantly Explicit",
  [IMPLICITNESS_EXPLICIT_HEAVY]: "Explicit Heavy",
  [IMPLICITNESS_SLIGHTLY_EXPLICIT]: "Slightly Explicit",
  [IMPLICITNESS_BALANCED]: "Balanced",
  [IMPLICITNESS_SLIGHTLY_IMPLICIT]: "Slightly Implicit",
  [IMPLICITNESS_IMPLICIT_HEAVY]: "Implicit Heavy",
  [IMPLICITNESS_PREDOMINANTLY_IMPLICIT]: "Predominantly Implicit",
  [IMPLICITNESS_MOSTLY_IMPLICIT]: "Mostly Implicit",
  [IMPLICITNESS_IMPLICIT_ONLY]: "Implicit Only",
});

export const SENTENCE_STRUCTURE_FULL_SENTENCE = "full_sentence" as const;
export const SENTENCE_STRUCTURE_NON_FINITE_CLAUSE = "non_finite_clause" as const;
export const SENTENCE_STRUCTURE_NOMINALIZATION = "nominalization" as const;

export const sentenceStructureOptions: Record<
  SentenceStructure,
  { label: string; description?: string }
> = Object.freeze({
  [SENTENCE_STRUCTURE_FULL_SENTENCE]: {
    label: "Full Sentence",
    description: "Sentence with subject and predicate starting with verb.",
  },
  [SENTENCE_STRUCTURE_NON_FINITE_CLAUSE]: {
    label: "Non-finite Clause",
    description:
      "Clauses using infinitives/participles (shorter, action-focused).",
  },
  [SENTENCE_STRUCTURE_NOMINALIZATION]: {
    label: "Nominalization",
    description: "Turns verbs/adjectives into nouns for compact statements.",
  },
});

export const ABSTRACTION_VERY_SPECIALIZED = "very_specialized" as const;
export const ABSTRACTION_MOSTLY_SPECIALIZED = "mostly_specialized" as const;
export const ABSTRACTION_PREDOMINANTLY_SPECIALIZED =
  "predominantly_specialized" as const;
export const ABSTRACTION_SPECIALIZATION_LEANING =
  "specialization_leaning" as const;
export const ABSTRACTION_SLIGHTLY_SPECIALIZED = "slightly_specialized" as const;
export const ABSTRACTION_ORIGINAL_SCOPE = "original_scope" as const;
export const ABSTRACTION_SLIGHTLY_GENERALIZED = "slightly_generalized" as const;
export const ABSTRACTION_GENERALIZATION_LEANING =
  "generalization_leaning" as const;
export const ABSTRACTION_PREDOMINANTLY_GENERALIZED =
  "predominantly_generalized" as const;
export const ABSTRACTION_MOSTLY_GENERALIZED = "mostly_generalized" as const;
export const ABSTRACTION_VERY_GENERALIZED = "very_generalized" as const;

export const degreeOfAbstractionOptions: Record<DegreeOfAbstraction, string> =
  Object.freeze({
    [ABSTRACTION_VERY_SPECIALIZED]: "Very Specialized",
    [ABSTRACTION_MOSTLY_SPECIALIZED]: "Mostly Specialized",
    [ABSTRACTION_PREDOMINANTLY_SPECIALIZED]: "Predominantly Specialized",
    [ABSTRACTION_SPECIALIZATION_LEANING]: "Specialization Leaning",
    [ABSTRACTION_SLIGHTLY_SPECIALIZED]: "Slightly Specialized",
    [ABSTRACTION_ORIGINAL_SCOPE]: "Original Scope",
    [ABSTRACTION_SLIGHTLY_GENERALIZED]: "Slightly Generalized",
    [ABSTRACTION_GENERALIZATION_LEANING]: "Generalization Leaning",
    [ABSTRACTION_PREDOMINANTLY_GENERALIZED]: "Predominantly Generalized",
    [ABSTRACTION_MOSTLY_GENERALIZED]: "Mostly Generalized",
    [ABSTRACTION_VERY_GENERALIZED]: "Very Generalized",
  });

export const INTENTION_STRICT_MAIN_AIM_ONLY =
  "strict_main_aim_only" as const;
export const INTENTION_MOSTLY_MAIN_AIM = "mostly_main_aim" as const;
export const INTENTION_PREDOMINANTLY_MAIN_AIM =
  "predominantly_main_aim" as const;
export const INTENTION_MAIN_AIM_LEANING = "main_aim_leaning" as const;
export const INTENTION_SLIGHTLY_MAIN_AIM = "slightly_main_aim" as const;
export const INTENTION_BALANCED_RELEVANCE = "balanced_relevance" as const;
export const INTENTION_SLIGHTLY_BROAD_CONTEXT =
  "slightly_broad_context" as const;
export const INTENTION_BROAD_CONTEXT_LEANING =
  "broad_context_leaning" as const;
export const INTENTION_PREDOMINANTLY_BROAD_CONTEXT =
  "predominantly_broad_context" as const;
export const INTENTION_MOSTLY_BROAD_CONTEXT =
  "mostly_broad_context" as const;
export const INTENTION_ALL_EVIDENCED_CONTEXT =
  "all_evidenced_context" as const;

export const degreeOfIntentionOptions: Record<DegreeOfIntention, string> =
  Object.freeze({
    [INTENTION_STRICT_MAIN_AIM_ONLY]: "Strict Main Aim Only",
    [INTENTION_MOSTLY_MAIN_AIM]: "Mostly Main Aim",
    [INTENTION_PREDOMINANTLY_MAIN_AIM]: "Predominantly Main Aim",
    [INTENTION_MAIN_AIM_LEANING]: "Main Aim Leaning",
    [INTENTION_SLIGHTLY_MAIN_AIM]: "Slightly Main Aim",
    [INTENTION_BALANCED_RELEVANCE]: "Balanced Relevance",
    [INTENTION_SLIGHTLY_BROAD_CONTEXT]: "Slightly Broad Context",
    [INTENTION_BROAD_CONTEXT_LEANING]: "Broad Context Leaning",
    [INTENTION_PREDOMINANTLY_BROAD_CONTEXT]: "Predominantly Broad Context",
    [INTENTION_MOSTLY_BROAD_CONTEXT]: "Mostly Broad Context",
    [INTENTION_ALL_EVIDENCED_CONTEXT]: "All Evidenced Context",
  });
